import { inject } from "@angular/core";
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import {
  Observable,
  from,
  throwError,
  switchMap,
  catchError,
  take,
  filter,
  timeout,
  of,
} from "rxjs";
import { Router } from "@angular/router";
import { FirebaseAuthService } from "../services/firebase-auth.service";
import { AuthConfigService } from "../services/auth-config.service";

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const firebaseAuth = inject(FirebaseAuthService);
  const router = inject(Router);
  const authConfig = inject(AuthConfigService);

  // Check if authentication should be enforced for this URL
  const shouldEnforce = authConfig.shouldEnforceAuth(req.url);

  if (!shouldEnforce) {
    console.log(`🔓 Skipping auth for: ${req.url}`);
    return next(req);
  }

  console.log(`🔐 Enforcing auth for: ${req.url}`);

  // Wait for Firebase auth state to be initialized and get the user
  return firebaseAuth.user$.pipe(
    filter((user) => user !== undefined), // Wait until auth state is resolved (not undefined)
    timeout(5000), // Add timeout to prevent hanging forever
    take(1), // Take only the first emission
    switchMap((user) => {
      console.log(
        `👤 User state for ${req.url}:`,
        user ? `Authenticated (${user.email})` : "Not authenticated",
      );

      if (!user) {
        // User is not authenticated - only redirect for certain URLs
        console.error(`❌ No user found for ${req.url}`);
        // Don't redirect if this is the roles endpoint being called after login
        if (!req.url.includes("/api/roles")) {
          router.navigate(["/login"]);
        }
        return throwError(() => new Error("User not authenticated"));
      }

      // Get Firebase ID token and add to request headers
      return from(user.getIdToken()).pipe(
        switchMap((token: string) => {
          console.log(
            `🔑 Token retrieved:`,
            token ? `${token.substring(0, 20)}...` : "NO TOKEN",
          );
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          console.log(`✅ Authorization header added to request`);
          return next(authReq);
        }),
      );
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle authentication errors
      // Only sign out for 401 errors (truly unauthorized)
      // 403 errors might just mean CORS issues or permission issues, not invalid auth
      if (error.status === 401) {
        // Token expired or invalid - redirect to login
        console.log("🚪 Received 401, signing out user");
        firebaseAuth.signOut();
        router.navigate(["/login"]);
      } else if (error.status === 403) {
        // Log 403 but don't sign out - might be CORS or permission issue
        console.warn("⚠️ Received 403 Forbidden for:", req.url);
      }
      return throwError(() => error);
    }),
  );
};
